import { createContext, ReactNode, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { z, ZodObject } from 'zod';
import * as Sentry from '@sentry/react';
import generateValidationSchema from '../helpers/schema';
import { extractFileFields, looksLikeFileValue, mapUploadedFiles, replaceUploadedFiles } from '../helpers/fileFields';
import { ApplicationConfigType, ApplicationType, ConfigType, FileType, FormDataType, FormType, OptionType, SectionType } from '../helpers/types';
import { useAuthContext } from './AuthProvider';
import { useConfigContext } from './ConfigProvider';
import { useToast } from '../components/ToastProvider';
import { useAuth } from 'react-oidc-context';
import { fetchTemplates, readOidcSub, toFlatApplicationConfig } from '../api/applicationTemplates';
import { collectAutoupdateElements, collectDefaultValueElements, getAutoupdateValue, isEmptyValue } from '../helpers/autoupdate';
import { clearActiveDraft, readActiveDraft, writeActiveDraft } from '../helpers/activeDraft';
import { sectionPath } from '../helpers/sectionPath';

type ContextType = {
    applicationId: number | null;
    applicationType: number | null;
    section: SectionType | null;
    form: FormType | null;
    formData: FormDataType;
    data: ConfigType | null;
    application: any;
    schema: z.ZodObject<any> | null;
    errors: FormDataType;
    consentApproved: boolean;
    contextDone: boolean;
    allValid: boolean;
    consentKine: boolean | undefined;
    setConsentKine: (value: boolean) => void;
    setConsentApproved: (value: boolean) => void;
    setApplicationData: (type: string) => void;
    setAllValid: (value: boolean) => void;
    next: () => void;
    prev: () => void;
    updateForm: (name: string, value: string | string[] | number | boolean | OptionType[] | FileType | null, isFile?: boolean) => void;
    updateErrors: (name: string, value: string | number | null) => void;
    saveDraft: () => Promise<boolean>;
    getDraftFromDataBase: () => Promise<string | null>;
    deleteDraftFromDataBase: (applicationType: number | null, draftId: number | null) => void;
    checkForIncompleteSection: (url: string) => Promise<string | null>;
    refreshApplicationConfig: () => Promise<void>;
    // DEV-only: hopper over `valid`/`allValid`-gating slik at "Neste" og
    // "Send inn" kan klikkes uten å fylle ut alle felter. Lagres i
    // localStorage så det persistent over refresh.
    debugBypass: boolean;
    setDebugBypass: (value: boolean) => void;
};

const NewApplicationContext = createContext<ContextType | null>(null);

const useNewApplicationContext = () => {
    const context = useContext(NewApplicationContext);
    if (context === null) {
        throw new Error('useNewApplicationContext must be used within a NewApplicationProvider');
    }

    return context;
};

/**
 * Slår `patch` inn i `prev`, men bare felt som fortsatt er tomme og faktisk
 * får en ny verdi. Endres ingenting, returneres `prev` uendret slik at React
 * ikke rerendrer (og autoupdate/defaultValue-effektene ikke går i loop).
 */
const applyPatchIfChanged = (prev: FormDataType, patch: FormDataType): FormDataType => {
    let next: FormDataType | null = null;
    for (const key of Object.keys(patch)) {
        const value = patch[key];
        if (prev[key] === value || !isEmptyValue(prev[key])) continue;
        if (!next) next = { ...prev };
        next[key] = value;
    }
    return next ?? prev;
};

const NewApplicationProvider = ({ children }: { children: ReactNode }) => {
    const { apiCall } = useAuthContext();
    const { t } = useTranslation();
    const { showToast } = useToast();
    const { data, draftId, setDraftId } = useConfigContext();
    const auth = useAuth();
    const [applicationsConfig, setApplicationsConfig] = useState<ApplicationConfigType[] | null>(null);
    const [applicationId, setApplicationId] = useState<number | null>(null);
    const [applicationType, setApplicationType] = useState<number | null>(null);
    const [templateId, setTemplateId] = useState<number | null>(null);

    const [consentKine, setConsentKine] = useState<boolean | undefined>(undefined);

    const [application, setApplication] = useState<ApplicationType | null>(null);
    const [formData, setFormData] = useState<FormDataType>({});
    const [lastSavedData, setLastSavedData] = useState<FormDataType>({});

    // saveDraft køes, slik NewReportProvider gjør. To lagringer som overlapper
    // ville ellers begge sendt samme File — lastet opp to ganger under ulik
    // `location`, der klienten satt igjen med den API-et ikke beholdt. Og uten
    // en id i ref ville begge opprettet hver sin kladd.
    const saveDraftQueueRef = useRef<Promise<boolean>>(Promise.resolve(true));
    const applicationIdRef = useRef<number | null>(null);
    // Alle File → lagret beskrivelse-bytter i denne økta. En lagring som
    // startet før forrige var ferdig, har fortsatt File-en i sin kopi av
    // skjemaet; den byttes her før sending, så fila ikke lastes opp igjen.
    const uploadedSwapsRef = useRef(new Map<File, Record<string, unknown>>());
    const [errors, setErrors] = useState<FormDataType>({});
    const [allValid, setAllValid] = useState<boolean>(false);

    const [contextDone, setContextDone] = useState(false);
    const [consentApproved, setConsentApproved] = useState(false);


    const [schema, setSchema] = useState<ZodObject<any> | null>(null);

    // DEV-debug: tilstand persistert i localStorage. Bare lest/skrevet i DEV-
    // build (Vite tree-shaker `import.meta.env.DEV`-greinen ut av produksjon).
    const [debugBypass, setDebugBypassState] = useState<boolean>(() => {
        if (!import.meta.env.DEV) return false;
        try {
            return localStorage.getItem('lyfti_debug_bypass') === 'true';
        } catch {
            return false;
        }
    });
    const setDebugBypass = (value: boolean) => {
        setDebugBypassState(value);
        if (import.meta.env.DEV) {
            try {
                localStorage.setItem('lyfti_debug_bypass', value ? 'true' : 'false');
            } catch {
                /* ignore */
            }
        }
    };

    const navigate = useNavigate();
    const location = useLocation();
    const params = useParams();

    // Gjeldende seksjon og skjemadel utledes av URL-en i samme render. De lå før
    // i state som effekter satte etter at URL-en hadde byttet, så hvert
    // skjermbytte ga én render med feil steg — FullscreenLoader eller forrige
    // seksjon uten innhold blinket forbi (ClickUp: «Flicker i søknadsportalen»).
    const section = useMemo<SectionType | null>(
        () => (application && params.section
            ? application.sections.find((s) => s.url === params.section) ?? null
            : null),
        [application, params.section]
    );
    const form = useMemo<FormType | null>(
        () => (params.form ? section?.forms?.find((f) => f.url === params.form) ?? null : null),
        [section, params.form]
    );

    /* const karCheck = async () => {
        if (formData?.account) {
            const karCheck = formData.kar_check || {};

            if (!karCheck.account || karCheck.account !== formData.account) {
                try {
                    const response = await apiCall({
                        action: 'checkKarAccount',
                        account: formData.account,
                        customer: formData.orgnr
                    })

                    formData.kar_check = response.data.data;
                } catch (error) {
                    console.error("Error fetching kar_check:", error);
                }
            }
        }
    } */

    /**
     * Henter applikasjons-config (templates + legacy meta) og oppdaterer state.
     * Returnerer den oppdaterte listen slik at kalleren (f.eks. refresh-knappen)
     * kan re-applisere `application` uten å vente på re-render.
     */
    const getApplicationConfig = async (): Promise<ApplicationConfigType[] | null> => {
        // 1) Prøv det nye REST-endepunktet først. Maler lagret i form-builder
        //    inneholder `restrictedVisibility` og andre nyere felt som legacy
        //    `portalConfig.payload.applications` ikke har — så vi må gå denne
        //    veien for at "Begrenset visning" skal fungere i applicant.
        const sub = auth?.user?.profile?.sub || readOidcSub();
        const hostname = localStorage.getItem('lyfti_selected_entity') || window.location.hostname;
        const templates = await fetchTemplates(sub, hostname);
        if (templates && templates.length) {
            // Map templates → ApplicationConfigType-shape. Vi kobler på legacy-
            // metadata (periods/deadline) ved å hente det fra config-payload
            // som faller ut fra apiCall under (samme datakilde som før).
            try {
                const legacyResponse = await apiCall({
                    action: 'getApplicationConfig',
                    version: data?.config_version,
                    id: draftId,
                });
                const legacyList: any[] = legacyResponse?.data?.status === 200
                    ? legacyResponse.data.data
                    : [];
                const legacyByType = new Map<number, Record<string, any>>();
                for (const at of legacyList) legacyByType.set(Number(at.type), at);

                const merged = templates.map((tpl) =>
                    toFlatApplicationConfig(tpl, legacyByType.get(Number(tpl.type)))
                );
                setApplicationsConfig(merged as any);
                return merged as any;
            } catch (err) {
                const merged = templates.map((tpl) => toFlatApplicationConfig(tpl));
                setApplicationsConfig(merged as any);
                return merged as any;
            }
        }

        // 2) Fallback: legacy endpoint hvis nytt REST-endepunkt er utilgjengelig
        //    eller entiteten ikke har noen maler i nye tabellen ennå.
        const response = await apiCall({
            action: 'getApplicationConfig',
            version: data?.config_version,
            id: draftId,
        });

        if (response?.data?.status === 200) {
            console.log('[NewApplicationProvider] applicationsConfig fra legacy fetchConfig');
            setApplicationsConfig(response.data.data);
            return response.data.data as ApplicationConfigType[];
        }
        return null;
    };

    /**
     * Re-henter config og oppdaterer `application` seamless slik at endringer
     * gjort i form-builder reflekteres i søker-portalen uten reload. Bevarer
     * `formData` og navigeringstilstand — bare skjemastrukturen (sections,
     * elements, restrictedVisibility, validation) byttes ut.
     */
    const refreshApplicationConfig = async (): Promise<void> => {
        const currentUrl = params.formType ?? application?.url;
        const updated = await getApplicationConfig();
        if (!currentUrl || !updated) return;
        const match = updated.find((a: ApplicationConfigType) => a.url === currentUrl);
        if (!match) return;
        const appData = match.application ?? match;
        setApplication({ ...appData, custom_progress: match.custom_progress });
        setApplicationType(match.type);
    };

    useEffect(() => {
        if (application) {
            if (params.formType) {
                const currentApplication = applicationsConfig?.filter((application: ApplicationConfigType) => application.url === params.formType);
                if (currentApplication) {
                    const appData = currentApplication[0].application ?? currentApplication[0];
                    setApplication({ ...appData, custom_progress: currentApplication[0].custom_progress });
                    setApplicationType(currentApplication[0].type);
                }
            }
        }
    }, [location]);

    useEffect(() => {
        if (application) {
            setSchema(generateValidationSchema(application));
        }
    }, [application]);

    // ---------------------------------------------------------------
    // `autoupdate`: auto-fyller felter fra dot-paths i formData når feltet
    // er tomt. F.eks. et felt med `autoupdate: "brreg.navn"` blir satt så
    // snart BRREG-respons er lagret i `formData.brreg`. Array støttes som
    // prioritert fallback — første ikke-tomme path vinner.
    //
    // Brukerens egne inputs overskrives ALDRI: vi sjekker `isEmptyValue`
    // for feltet selv før vi skriver.
    // ---------------------------------------------------------------
    useEffect(() => {
        if (!application) return;
        const elements = collectAutoupdateElements(application);
        if (elements.length === 0) return;

        // Bygg en patch med alle felt som bør auto-fylles på dette tidspunktet.
        // Vi setter alt i ÉN setFormData-call slik at vi unngår å trigge én
        // re-render per felt — og slik at den underliggende formData-snapshoten
        // er konsistent når vi sjekker hvilke felter som mangler verdi.
        const patch: FormDataType = {};
        for (const el of elements) {
            if (!isEmptyValue(formData[el.name])) continue;
            const value = getAutoupdateValue((el as any).autoupdate, formData);
            // Tomme verdier (tom streng / tom liste) patches ikke: feltet forblir
            // tomt, og en ny formData-referanse ville trigget effekten i det
            // uendelige («Maximum update depth exceeded»).
            if (value === undefined || isEmptyValue(value)) continue;
            patch[el.name] = value;
        }

        if (Object.keys(patch).length === 0) return;
        setFormData((prev) => applyPatchIfChanged(prev, patch));
    }, [formData, application]);

    // ---------------------------------------------------------------
    // `defaultValue` (predefinert verdi): seedes inn i formData når feltet er
    // tomt. Hovedbruk er `hidden`-felt som bare skal ha én mulig verdi (f.eks.
    // `category`/`subtype` som er påkrevd i API/DB) — settes uten å vises.
    // Samme tomhetsgaranti som autoupdate: brukerens egne verdier overskrives
    // aldri.
    // ---------------------------------------------------------------
    useEffect(() => {
        if (!application) return;
        const elements = collectDefaultValueElements(application);
        if (elements.length === 0) return;

        const patch: FormDataType = {};
        for (const el of elements) {
            if (!isEmptyValue(formData[el.name])) continue;
            const value = (el as any).defaultValue;
            if (value === undefined || isEmptyValue(value)) continue;
            patch[el.name] = value;
        }

        if (Object.keys(patch).length === 0) return;
        setFormData((prev) => applyPatchIfChanged(prev, patch));
    }, [formData, application]);

    useEffect(() => {
        if (applicationType) setContextDone(true);
    }, [applicationType]);

    useEffect(() => {
        if (draftId === null && params.section && params.formType) {
            const stored = readActiveDraft(params.formType);
            if (stored) setDraftId(stored);
        }
    }, []);

    useEffect(() => {
        if (applicationId && params.formType) {
            writeActiveDraft(params.formType, applicationId);
        }
    }, [applicationId]);

    useEffect(() => {
        getApplicationConfig();
    }, [draftId]);

    return (
        <NewApplicationContext.Provider
            value={{
                applicationId,
                applicationType,
                section,
                form,
                formData,
                data,
                application,
                schema,
                errors,
                consentApproved,
                contextDone,
                allValid,
            consentKine,
            setConsentKine,
                setAllValid,
                setConsentApproved,
                setApplicationData: (type) => {
                    if (applicationsConfig) {
                        const currentApplication = applicationsConfig.filter((application: ApplicationConfigType) => application.url === type);
                        const appData = currentApplication[0].application ?? currentApplication[0];
                        setApplication(appData);
                        setApplicationType(currentApplication[0].type);
                        setTemplateId(currentApplication[0].id ?? null);
                    }
                },
                next: () => {
                    //karCheck();
                    if (!section) {
                        if (application?.sections[0]) {
                            navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[0])}`);
                        }
                        return;
                    }

                    if (form) {
                        const formIndex = section?.forms.findIndex((f) => f.url === form.url);
                        if (formIndex !== undefined && section?.forms[formIndex + 1]) {
                            navigate(`/soknadskjema/${application?.url}/${section.url}/${section.forms[formIndex + 1].url}`);
                        } else {
                            const sectionIndex = application?.sections.findIndex((s) => s.url === section?.url);
                            if (sectionIndex !== undefined && application?.sections[sectionIndex + 1]) {
                                // Første skjema i neste seksjon, ellers bare seksjonen
                                navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[sectionIndex + 1])}`);
                            }
                        }
                    } else {
                        const sectionIndex = application?.sections.findIndex((s) => s.url === section.url);
                        if (sectionIndex !== undefined && application?.sections[sectionIndex + 1]) {
                            navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[sectionIndex + 1])}`);
                        }
                    }
                },
                prev: () => {
                    if (form && section && application) {
                        const formIndex = section.forms.findIndex((f) => f.url === form.url);
                        if (formIndex !== undefined && section.forms[formIndex - 1]) {
                            navigate(`/soknadskjema/${application.url}/${section.url}/${section.forms[formIndex - 1].url}`);
                        } else {
                            const sectionIndex = application.sections.findIndex((s) => s.url === section.url);
                            if (application.sections[sectionIndex - 1]) {
                                // Siste skjema i forrige seksjon, ellers bare seksjonen
                                navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[sectionIndex - 1], 'last')}`);
                            }
                        }
                    } else {
                        if (section && application && section.type == 'modal') {
                            //gå til forrige section og siste url
                            const sectionIndex = application.sections.findIndex((s) => s.url === section.url);

                            if (application.sections[sectionIndex - 1]) {
                                navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[sectionIndex - 1], 'last')}`);
                            }
                        } else if (section && application) {
                            const sectionIndex = application.sections.findIndex((s) => s.url === section.url);
                            if (application.sections[sectionIndex - 1]) {
                                navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[sectionIndex - 1])}`);
                            }
                        }
                    }
                },
                updateForm: (field: string, value: string | number | boolean | OptionType[] | FileType | null) => {
                    setFormData((prev) => {
                        let newFormData;

                        if (value === null) {
                            // If value is null, remove the field from the state object
                            const { [field]: _, ...remainingFields } = prev;
                            newFormData = remainingFields;
                        } else {
                            // Otherwise, update the state as usual
                            newFormData = {
                                ...prev,
                                [field]: value,
                            };
                        }
                        return newFormData;
                    });
                },
                updateErrors: (field: string, value: string | number | null) => {
                    setErrors((prev) => {
                        if (value === null) {
                            // If value is null, remove the field from the state object
                            const { [field]: _, ...newErrorsData } = prev;
                            return newErrorsData;
                        }

                        // Otherwise, update the state as usual
                        return {
                            ...prev,
                            [field]: value,
                        };
                    });
                },
                saveDraft: () => {
                  const run = async (): Promise<boolean> => {
                    const currentData = replaceUploadedFiles(formData, uploadedSwapsRef.current);
                    const currentId = applicationIdRef.current ?? applicationId;

                    // Prevent saving if data hasn't changed
                    if (JSON.stringify(currentData) === JSON.stringify(lastSavedData)) return true;

                    let object: Record<string, any> = {};
                    // Filfelter: skill nye File-objekter (lastes opp som $_FILES) fra
                    // allerede-lagrede filbeskrivelser. De eksisterende sendes som
                    // `existing_attachments` slik at API-et vet hvilke som fortsatt skal
                    // beholdes — og dermed kan fjerne dem søkeren har slettet.
                    //
                    // Hvilke felter dette gjelder kommer fra SKJEMAMALEN, ikke fra
                    // feltnavnet. Feltnavn er fritekst stiftelsen skriver i
                    // lyfti-form-builder, og default-navnet den gir er `newFileupload1`.
                    // Med den gamle `filer_*`-testen havnet slike felter i `else`-grenen
                    // og ble serialisert som `newFileupload1[0][name]` — altså lagrede
                    // filbeskrivelser rett inn i $_POST og videre inn i søknadens
                    // payload, der de gjenopplivet filer søkeren nettopp hadde fjernet.
                    const fileFieldNames = new Set(extractFileFields(application).map((f) => f.name));
                    const isFileField = (key: string, value: unknown) =>
                        fileFieldNames.has(key) || key.startsWith('filer_') || looksLikeFileValue(value);

                    const existingAttachments: Record<string, any[]> = {};
                    for (let key in currentData) {
                        const value = currentData[key];

                        if (isFileField(key, value)) {
                            const arr = Array.isArray(value) ? value : (value ? [value] : []);
                            const newFiles = arr.filter((v: any) => v instanceof File);
                            const existing = arr.filter((v: any) => v && !(v instanceof File));
                            if (newFiles.length) object[key] = newFiles;   // → $_FILES
                            existingAttachments[key] = existing;            // beholdes (kan være tom)
                        } else {
                            object[key] = value;
                        }
                    }

                    try {
                        // Save draft to server
                        const response = await apiCall({
                            action: 'saveApplicationDraft',
                            applicationId: currentId,
                            applicationType: applicationType,
                            templateId: templateId,
                            existing_attachments: JSON.stringify(existingAttachments),
                            profile: {
                                nin: auth?.user?.profile?.nin,
                                name: auth?.user?.profile?.name
                            },
                            ...object,
                        });

                        // apiCall gir undefined når selve kallet feilet — den har da
                        // allerede vist feilmeldingen og meldt fra til Sentry.
                        if (!response) return false;

                        if (!currentId && response.data.application_id) {
                            applicationIdRef.current = response.data.application_id;
                            setApplicationId(response.data.application_id);
                        }
                        if (response && response.data && response.data.status && response.data.status == 1138) {
                            showToast(t('application_already_submitted'), 'warning');
                        }

                        if (response.data.status !== 200) {
                            Sentry.captureMessage('saveApplicationDraft avvist av API-et', {
                                level: 'warning',
                                tags: { api_action: 'saveApplicationDraft', api_status: String(response.data.status) },
                            });
                            showToast(t('error_generic_api'), 'danger');
                            return false;
                        }

                        // Filene som ble lastet opp byttes mot det API-et lagret,
                        // ellers sendes de på nytt ved hver lagring.
                        const swaps = mapUploadedFiles(object, response.data.data?.uploaded);
                        if (swaps.size > 0) {
                            swaps.forEach((stored, file) => uploadedSwapsRef.current.set(file, stored));
                            setFormData((prev) => replaceUploadedFiles(prev, swaps));
                        }

                        // Update the last saved data
                        setLastSavedData(replaceUploadedFiles(currentData, swaps));

                        return true;
                    } catch (error) {
                        Sentry.captureException(error, { tags: { api_action: 'saveApplicationDraft' } });
                        showToast(t('error_generic_api'), 'danger');
                        console.error('Error saving draft:', error);
                        return false;
                    }
                  };

                  const next = saveDraftQueueRef.current.then(run, run);
                  saveDraftQueueRef.current = next;
                  return next;
                },
                getDraftFromDataBase: async () => {
                    const response = await apiCall({
                        action: 'getApplicationDraftFromDB',
                        applicationId: draftId,
                    });

                    if (!response?.data?.data) {
                        if (params.formType) clearActiveDraft(params.formType);
                        setDraftId(null);
                        return null;
                    }

                    const data = response.data.data;
                    setFormData(data);
                    applicationIdRef.current = draftId;
                    setApplicationId(draftId);

                    if (!application || !schema) {
                        console.warn('Application or schema is missing.');
                        return null;
                    }

                    let lastIncompleteUrl: string | null = null;

                    // Iterate through sections and forms
                    for (const section of application.sections) {
                        if (!section.forms || section.forms.length === 0) {
                            continue;
                        }

                        for (const form of section.forms) {
                            const sectionSchema = schema.shape[section.url];
                            const formSchema = sectionSchema.shape[form.url] ?? sectionSchema._def?.schema?.shape[form.url];

                            if (!formSchema) {
                                console.warn(`Schema missing for form: ${form.url}`);
                                continue;
                            }

                            // Transforming some values from database
                            form.elements.forEach((element) => {
                                if (data[element.name]) {
                                    switch (element.type) {
                                        case 'dropdowncheck': {
                                            const raw = data[element.name];
                                            let parsed: string[];
                                            if (typeof raw === 'string') {
                                                const arr = JSON.parse(raw);
                                                parsed = Array.isArray(arr) && arr.length > 0 && typeof arr[0] === 'object'
                                                    ? arr.map((o: any) => o.value)
                                                    : arr ?? [];
                                            } else if (Array.isArray(raw)) {
                                                parsed = raw.length > 0 && typeof raw[0] === 'object'
                                                    ? raw.map((o: any) => o.value)
                                                    : raw;
                                            } else {
                                                parsed = [];
                                            }
                                            data[element.name] = parsed;
                                            setFormData((prevData) => ({ ...prevData, [element.name]: parsed }));
                                            break;
                                        }
                                        case 'budgetlines':
                                        case 'expensetable':
                                        case 'repeater': {
                                            const raw = data[element.name];
                                            if (raw) {
                                                const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
                                                if (Array.isArray(parsed)) {
                                                    data[element.name] = parsed;
                                                    setFormData((prevData) => ({ ...prevData, [element.name]: parsed }));
                                                }
                                            }
                                            break;
                                        }
                                        case 'fileuploadsingle': {
                                            const newValue = data[element.name][0];
                                            data[element.name] = newValue;
                                            setFormData((prevData) => ({
                                                ...prevData,
                                                [element.name]: newValue,
                                            }));
                                            break;
                                        }
                                    }
                                }
                            });

                            // Validate the form data
                            const valid = formSchema.safeParse(data);
                            if (!valid.success) {
                                lastIncompleteUrl = `${section.url}/${form.url}`;
                                return lastIncompleteUrl; // Navigate to the first invalid form
                            }
                        }
                    }

                    // If all forms are valid, return the last form URL
                    if (application.sections.length > 0) {
                        const lastSection = application.sections.at(-1);
                        //const lastForm = lastSection?.forms?.at(-1);
                        if (lastSection) {
                            lastIncompleteUrl = `${lastSection.url}`;
                        }
                    }

                    return lastIncompleteUrl;
                },
                deleteDraftFromDataBase: async (applicationType, draftId) => {
                    const response = await apiCall({
                        action: 'deleteApplicationDraftFromDB',
                        draftId,
                        applicationType,
                    });

                    if (response?.data?.status === 200) {
                        if (params.formType && draftId) clearActiveDraft(params.formType, draftId);
                        setDraftId(null);

                        if (application?.sections[0]?.forms?.length) {
                            navigate(`/soknadskjema/${application.url}/${sectionPath(application.sections[0])}`);
                        }
                    }

                    return response;
                },
                checkForIncompleteSection: async (sectionUrl: string) => {
                    if (!application || !schema) {
                        console.warn('Application or schema is missing.');
                        return null;
                    }

                    const isSummary = sectionUrl === 'oppsummering';

                    // Find the section we want to navigate to
                    if (!isSummary) {
                        const targetSection = application.sections.find((s) => s.url === sectionUrl);
                        if (!targetSection || !targetSection.forms || targetSection.forms.length === 0) {
                            return null;
                        }
                    }

                    // Return first form of target section if all previous sections are valid
                    for (const section of application.sections) {
                        if (!section.forms || section.forms.length === 0) {
                            continue;
                        }

                        for (const form of section.forms) {
                            const sectionSchema = schema.shape[section.url];
                            const formSchema = sectionSchema.shape[form.url] ?? sectionSchema._def?.schema?.shape[form.url];

                            if (!formSchema) {
                                console.warn(`Schema missing for form: ${form.url}`);
                                continue;
                            }

                            // Validate the form data
                            const valid = formSchema.safeParse(formData);
                            if (!valid.success) {
                                // If invalid form is in current section, return its URL
                                if (section.url === sectionUrl) {
                                    return `${section.url}/${form.url}`;
                                }
                                // If invalid form is in previous section, prevent navigation
                                return null;
                            }
                        }

                        // If we've reached the target section and all previous validations passed
                        if (section.url === sectionUrl) {
                            return sectionPath(section);
                        }
                    }

                    // If all previous sections are valid and the target is summary, allow navigation
                    if (isSummary) {
                        return sectionUrl;
                    }

                    return null;
                },
                refreshApplicationConfig,
                debugBypass,
                setDebugBypass,
            }}
        >
            {children}
        </NewApplicationContext.Provider>
    );
};

export { NewApplicationContext, NewApplicationProvider, useNewApplicationContext };
